import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Button,
    Space,
    Tag,
    Modal,
    Form,
    DatePicker,
    TimePicker,
    Select,
    Input,
    InputNumber,
    Row,
    Col,
    message,
    Popconfirm,
    Descriptions,
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    SearchOutlined,
    ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as adminactions from '../../store/actions/AuthActions';
import useUrlFilters from 'hooks/useUrlFilters';

const { RangePicker } = DatePicker;

// Applied filters live in the URL (prefixed, as this renders inside the employee detail page).
const URL_FILTERS = {
    range: { type: 'dateRange', keys: ['date_from', 'date_to'] },
    status: { type: 'string' },
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 10 },
};

const EmployeeAttendance = ({ employeeId, showAddButton = true, urlPrefix = 'att_' }) => {
    const organization = useSelector((state) => state.auth.organization);
    const [form] = Form.useForm();
    const [filterForm] = Form.useForm();

    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS, { prefix: urlPrefix });
    const filters = useMemo(() => {
        const searchFilters = {};
        if (urlFilters.range) {
            searchFilters.date_from = urlFilters.range[0].format('YYYY-MM-DD');
            searchFilters.date_to = urlFilters.range[1].format('YYYY-MM-DD');
        }
        if (urlFilters.status) {
            searchFilters.status = urlFilters.status;
        }
        return searchFilters;
    }, [urlFilters]);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [saving, setSaving] = useState(false);
    const [shifts, setShifts] = useState([]);

    const loadData = async (page = 1, pageSize = 10, searchFilters = {}) => {
        if (!employeeId) return;
        setLoading(true);
        try {
            const response = await adminactions.loadAttendanceRecords(
                page,
                pageSize,
                organization,
                { employee: employeeId, ...searchFilters }
            );
            if (response && response.results) {
                setAttendanceData(response.results);
                setPagination({
                    current: page,
                    pageSize: pageSize,
                    total: response.count,
                });
            }
        } catch (error) {
            message.error('Failed to load attendance records');
            console.error('Error loading attendance:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadShifts = async () => {
        try {
            const response = await adminactions.loadWorkShifts(1, 1000, organization);
            if (response && response.results) {
                setShifts(response.results);
            }
        } catch (error) {
            console.error('Error loading shifts:', error);
        }
    };

    useEffect(() => {
        if (employeeId && organization) {
            loadShifts();
        }
    }, [employeeId, organization]);

    useEffect(() => {
        if (employeeId && organization) {
            loadData(urlFilters.page, Math.min(urlFilters.pageSize, MAX_PAGE_SIZE), filters);
        }
    }, [employeeId, organization, urlFilters, filters]);

    // Keep the filter form in sync with the URL (shared links, back/forward)
    useEffect(() => {
        filterForm.setFieldsValue({
            dateRange: urlFilters.range || undefined,
            status: urlFilters.status || undefined,
        });
    }, [urlFilters]);

    const handleTableChange = (newPagination) => {
        // A new page size starts again from page 1.
        const sizeChanged = newPagination.pageSize !== pagination.pageSize;
        setUrlFilters({ page: sizeChanged ? 1 : newPagination.current, pageSize: newPagination.pageSize });
    };

    const handleSearch = () => {
        const values = filterForm.getFieldsValue();
        setUrlFilters({
            range: values.dateRange && values.dateRange.length === 2 ? values.dateRange : null,
            status: values.status || null,
            page: 1,
        });
    };

    const handleClearFilters = () => {
        filterForm.resetFields();
        setUrlFilters({ range: null, status: null, page: 1 });
    };

    const handleDelete = async (id) => {
        try {
            await adminactions.deleteAttendance(id);
            message.success('Attendance record deleted successfully');
            loadData(pagination.current, pagination.pageSize, filters);
        } catch (error) {
            message.error('Failed to delete attendance record');
        }
    };

    const handleAdd = () => {
        setModalMode('add');
        setSelectedRecord(null);
        form.resetFields();
        form.setFieldsValue({
            status: 'present',
            total_hours_worked: 0,
            overtime_hours: 0,
            late_arrival_minutes: 0,
            early_departure_minutes: 0,
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setModalMode('edit');
        setSelectedRecord(record);
        form.setFieldsValue({
            date: record.date ? dayjs(record.date) : null,
            shift: record.shift,
            first_in: record.first_in ? dayjs(record.first_in, 'HH:mm:ss') : null,
            last_out: record.last_out ? dayjs(record.last_out, 'HH:mm:ss') : null,
            status: record.status,
            total_hours_worked: record.total_hours_worked,
            overtime_hours: record.overtime_hours,
            late_arrival_minutes: record.late_arrival_minutes,
            early_departure_minutes: record.early_departure_minutes,
            notes: record.notes,
        });
        setModalVisible(true);
    };

    const handleView = (record) => {
        setModalMode('view');
        setSelectedRecord(record);
        setModalVisible(true);
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setSelectedRecord(null);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            const payload = {
                employee: employeeId,
                date: values.date.format('YYYY-MM-DD'),
                shift: values.shift || null,
                first_in: values.first_in ? values.first_in.format('HH:mm:ss') : null,
                last_out: values.last_out ? values.last_out.format('HH:mm:ss') : null,
                status: values.status,
                total_hours_worked: values.total_hours_worked || 0,
                overtime_hours: values.overtime_hours || 0,
                late_arrival_minutes: values.late_arrival_minutes || 0,
                early_departure_minutes: values.early_departure_minutes || 0,
                notes: values.notes || '',
            };

            if (modalMode === 'edit') {
                await adminactions.updateAttendance(selectedRecord.id, payload);
                message.success('Attendance record updated successfully');
            } else {
                await adminactions.createAttendance(payload);
                message.success('Attendance record created successfully');
            }

            setModalVisible(false);
            form.resetFields();
            setSelectedRecord(null);
            loadData(pagination.current, pagination.pageSize, filters);
        } catch (error) {
            if (error.errorFields) return; // form validation error
            message.error(modalMode === 'edit' ? 'Failed to update attendance' : 'Failed to create attendance');
            console.error('Error saving attendance:', error);
        } finally {
            setSaving(false);
        }
    };

    const statusColors = {
        present: 'green',
        absent: 'red',
        leave: 'orange',
        holiday: 'blue',
        partial: 'gold',
        partial_day: 'gold',
        late_arrival: 'volcano',
        early_departure: 'purple',
        hours_deficit: 'magenta',
        weekend: 'cyan',
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            sorter: true,
        },
        {
            title: 'Shift',
            dataIndex: 'shift_name',
            key: 'shift',
            render: (text) => text || '-',
        },
        {
            title: 'Check In',
            dataIndex: 'first_in',
            key: 'first_in',
            render: (text) => text || '-',
        },
        {
            title: 'Check Out',
            dataIndex: 'last_out',
            key: 'last_out',
            render: (text) => text || '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={statusColors[status] || 'default'}>
                    {status?.replace(/_/g, ' ').toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Hours Worked',
            dataIndex: 'total_hours_worked',
            key: 'total_hours_worked',
            render: (hours) => hours || '0',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record)}
                        title="View"
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        title="Edit"
                    />
                    <Popconfirm
                        title="Are you sure you want to delete this record?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} title="Delete" />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const renderModalContent = () => {
        if (modalMode === 'view' && selectedRecord) {
            return (
                <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label="Date">
                        {selectedRecord.date}
                    </Descriptions.Item>
                    <Descriptions.Item label="Shift">
                        {selectedRecord.shift_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag color={statusColors[selectedRecord.status] || 'default'}>
                            {selectedRecord.status?.replace(/_/g, ' ').toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Recorded By">
                        {selectedRecord.recorded_by?.replace(/_/g, ' ') || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Check In">
                        {selectedRecord.first_in || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Check Out">
                        {selectedRecord.last_out || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Hours Worked">
                        {selectedRecord.total_hours_worked || 0} hours
                    </Descriptions.Item>
                    <Descriptions.Item label="Overtime Hours">
                        {selectedRecord.overtime_hours || 0} hours
                    </Descriptions.Item>
                    <Descriptions.Item label="Late Arrival">
                        {selectedRecord.late_arrival_minutes || 0} minutes
                    </Descriptions.Item>
                    <Descriptions.Item label="Early Departure">
                        {selectedRecord.early_departure_minutes || 0} minutes
                    </Descriptions.Item>
                    <Descriptions.Item label="Notes" span={2}>
                        {selectedRecord.notes || '-'}
                    </Descriptions.Item>
                </Descriptions>
            );
        }

        return (
            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="date"
                            label="Date"
                            rules={[{ required: true, message: 'Please select a date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="shift" label="Shift">
                            <Select placeholder="Select Shift" allowClear>
                                {shifts.map((shift) => (
                                    <Select.Option key={shift.id} value={shift.id}>
                                        {shift.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="status"
                            label="Status"
                            rules={[{ required: true, message: 'Please select a status' }]}
                        >
                            <Select placeholder="Select Status">
                                <Select.Option value="present">Present</Select.Option>
                                <Select.Option value="absent">Absent</Select.Option>
                                <Select.Option value="leave">Leave</Select.Option>
                                <Select.Option value="holiday">Holiday</Select.Option>
                                <Select.Option value="partial">Partial Day</Select.Option>
                                <Select.Option value="late">Late Arrival</Select.Option>
                                <Select.Option value="early">Early Departure</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="first_in" label="Check In Time">
                            <TimePicker style={{ width: '100%' }} format="HH:mm:ss" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="last_out" label="Check Out Time">
                            <TimePicker style={{ width: '100%' }} format="HH:mm:ss" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="total_hours_worked" label="Total Hours Worked">
                            <InputNumber min={0} step={0.25} style={{ width: '100%' }} placeholder="0" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="overtime_hours" label="Overtime Hours">
                            <InputNumber min={0} step={0.25} style={{ width: '100%' }} placeholder="0" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="late_arrival_minutes" label="Late Arrival (Minutes)">
                            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="early_departure_minutes" label="Early Departure (Minutes)">
                            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="notes" label="Notes">
                            <Input.TextArea rows={3} placeholder="Enter any notes..." />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        );
    };

    return (
        <div>
            {/* Filter Bar */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <Form form={filterForm} layout="inline" style={{ flexWrap: 'wrap', gap: 8 }}>
                    <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
                        <RangePicker />
                    </Form.Item>
                    <Form.Item name="status" style={{ marginBottom: 0 }}>
                        <Select placeholder="Status" allowClear style={{ width: 150 }}>
                            <Select.Option value="present">Present</Select.Option>
                            <Select.Option value="absent">Absent</Select.Option>
                            <Select.Option value="leave">Leave</Select.Option>
                            <Select.Option value="holiday">Holiday</Select.Option>
                            <Select.Option value="partial">Partial Day</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                                Search
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
                                Clear
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
                {showAddButton && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Add Attendance
                    </Button>
                )}
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={attendanceData}
                loading={loading}
                rowKey="id"
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                }}
                onChange={handleTableChange}
                scroll={{ x: 800 }}
                size="middle"
            />

            {/* Add/Edit/View Modal */}
            <Modal
                title={
                    modalMode === 'view'
                        ? 'View Attendance'
                        : modalMode === 'edit'
                        ? 'Edit Attendance'
                        : 'Add Attendance'
                }
                open={modalVisible}
                onCancel={handleModalCancel}
                onOk={modalMode === 'view' ? handleModalCancel : handleSubmit}
                okText={modalMode === 'view' ? 'Close' : modalMode === 'edit' ? 'Update' : 'Save'}
                confirmLoading={saving}
                width={700}
                destroyOnClose
            >
                {renderModalContent()}
            </Modal>
        </div>
    );
};

export default EmployeeAttendance;
